import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal, Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/common';
import type { FrontlineUserAdminDto, FrontlineVerificationEventType } from '@/api/types';
import {
  useFrontlineVerificationHistory, useMarkFrontlineEmailVerified, useSendFrontlineVerification,
} from '@/hooks/useFrontlineUsersAdmin';
import { frontlineErrorKey } from './frontlineErrors';

const TITLE_ID = 'gu-verif-title';

interface Props {
  user: FrontlineUserAdminDto;
  onClose: () => void;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}

/**
 * Detalle de verificación: estado, historial de eventos (del más reciente al más antiguo) y las dos acciones que
 * el administrador puede hacer — enviar/reenviar el enlace y marcar el correo como verificado a mano.
 *
 * «Marcar como verificado» es una excepción a la regla de que verificar prueba que la persona controla el buzón:
 * pide confirmación y deja el rastro con el nombre de quien lo hizo. Está deshabilitado en las fichas sin reclamar
 * (su camino es el correo de activación, que fija la contraseña).
 */
export function VerificationModal({ user, onClose, onDone, onError }: Readonly<Props>) {
  const { t, i18n } = useTranslation();
  const history = useFrontlineVerificationHistory(user.id);
  const send = useSendFrontlineVerification();
  const mark = useMarkFrontlineEmailVerified();
  const [confirming, setConfirming] = useState(false);

  const hasEmail = !!user.correoPersonal;
  const verified = user.estado === 'Verificado';
  // Una cuenta de SSO entra con su cuenta corporativa: no se le envía ni se le verifica nada desde aquí.
  const delAd = user.estado === 'DelDirectorio';
  const canSend = hasEmail && !verified && !delAd;
  const canMark = canSend && user.reclamada;
  let markTitle: string | undefined;
  if (delAd) markTitle = t('frontlineUsers.err.directory');
  else if (canSend && !user.reclamada) markTitle = t('frontlineUsers.markUnclaimedTitle');

  const doSend = async () => {
    try {
      const result = await send.mutateAsync(user.id);
      onDone(t('frontlineUsers.sentOk', { email: result.enviadoA }));
      onClose();
    } catch (err) {
      onError(t(frontlineErrorKey(err)));
    }
  };

  const doMark = async () => {
    setConfirming(false);
    try {
      await mark.mutateAsync(user.id);
      onDone(t('frontlineUsers.markedOk', { name: user.nombre }));
      onClose();
    } catch (err) {
      onError(t(frontlineErrorKey(err)));
    }
  };

  const when = (iso: string) => new Date(iso).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });
  const eventLabel = (tipo: FrontlineVerificationEventType) => t(`frontlineUsers.event.${tipo}`);

  return (
    <>
      <Modal open onClose={onClose} maxWidth={520} labelledBy={TITLE_ID}>
        <ModalHeader onClose={onClose}><h2 id={TITLE_ID}>{t('frontlineUsers.verTitle', { name: user.nombre })}</h2></ModalHeader>
        <ModalBody>
          <div className="gu-ficha">
            {user.nomina && <span className="gu-tag gu-tag--payroll">{user.nomina}</span>}
            {user.ou && <span className="gu-tag gu-tag--ou">{user.ou}</span>}
            {user.sitio && <span className="gu-tag gu-tag--site">{user.sitio}</span>}
          </div>

          <div className="form-group">
            <span className="gu-label">{delAd ? t('frontlineUsers.colCorp') : t('frontlineUsers.colPersonal')}</span>
            <div>{user.correoPersonal ?? <span className="gu-empty">{t('frontlineUsers.noEmailTitle')}</span>}</div>
          </div>
          <div className="form-group">
            <span className="gu-label">{t('frontlineUsers.colState')}</span>
            <div><span className={`gu-state gu-st--${user.estado}`}>{t(`frontlineUsers.state.${user.estado}`)}</span></div>
          </div>

          <div className="gu-history">
            <div className="gu-section-title">{t('frontlineUsers.history')}</div>
            {history.isLoading && <i className="fas fa-spinner fa-spin" aria-hidden="true" />}
            {history.isError && <p className="gu-help gu-help--error" role="alert">{t('frontlineUsers.err.generic')}</p>}
            {history.data && history.data.length === 0 && <p className="gu-help">{t('frontlineUsers.noEvents')}</p>}
            {history.data && history.data.length > 0 && (
              <ul className="gu-timeline">
                {history.data.map((ev, i) => (
                  <li key={`${ev.ocurridoEn}-${ev.tipo}-${i}`}>
                    {eventLabel(ev.tipo)}
                    {ev.actorNombre && <span className="gu-by"> {t('frontlineUsers.eventBy', { name: ev.actorNombre })}</span>}
                    <time dateTime={ev.ocurridoEn}>{when(ev.ocurridoEn)}</time>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {markTitle && <p className="gu-help gu-help--off">{markTitle}</p>}
        </ModalBody>
        <ModalFooter>
          <button type="button" className="btn btn-secondary gu-btn" disabled={!canMark || mark.isPending}
            title={markTitle}
            onClick={() => setConfirming(true)}>
            <i className="fas fa-check" /> {t('frontlineUsers.markVerified')}
          </button>
          <button type="button" className="btn btn--primary" disabled={!canSend || send.isPending}
            title={delAd ? t('frontlineUsers.err.directory') : undefined} onClick={() => void doSend()}>
            <i className={`fas ${send.isPending ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} />{' '}
            {user.estado === 'Pendiente' ? t('frontlineUsers.verResend') : t('frontlineUsers.colSend')}
          </button>
        </ModalFooter>
      </Modal>

      {confirming && (
        <ConfirmModal
          type="warning"
          title={t('frontlineUsers.markTitle')}
          message={t('frontlineUsers.markMsg')}
          confirmText={t('frontlineUsers.markVerified')}
          cancelText={t('common.cancel')}
          onConfirm={() => void doMark()}
          onClose={() => setConfirming(false)}
        />
      )}
    </>
  );
}
